import * as React from 'react'
import { useDropzone } from 'react-dropzone'
import jpngirlModel from '../fixtures/models/jpngirl01.mdl'
import leetModel from '../fixtures/models/leet.mdl'
import vAk47Model from '../fixtures/models/v_ak47.mdl'
import { ViewMode } from '../core/mdlConstants'
import { ModelController } from '../core/modelController'
import { ModelData } from '../core/modelDataParser'
import { BackgroundContainer } from './BackgroundContainer'
import { Controller } from './Controller'
import { Dropzone } from './Dropzone'
import { FileContainer } from './FileContainer'
import { FpsCounter } from './FpsCounter'
import { GlobalStyles } from './GlobalStyles'
import { LoadingScreen } from './LoadingScreen'
import {
  cacheRemoteModelUrl,
  getModelCacheStats,
  isModelCacheId,
  resolveCachedModel,
  type ModelCacheStats
} from './modelCache'
import { DemoModelOption, StartScreen } from './StartScreen'
import {
  createHomeUrl,
  createUrlWithoutShareParams,
  createUrlWithModelQuery,
  parseModelUrl,
  parseUrlConfig,
  type CameraUrlPreset,
  type PlaybackUrlPreset
} from './urlConfig'
import type { ViewerPose } from './Renderer'

const Renderer = React.lazy(() => import('./Renderer').then(module => ({ default: module.Renderer })))

type DemoModel = DemoModelOption & {
  preferredViewMode?: ViewMode
}

const DEMO_MODELS: DemoModel[] = [
  {
    id: 'leet',
    label: 'Leet',
    fileName: 'leet.mdl',
    url: leetModel
  },
  {
    id: 'jpngirl01',
    label: 'JPNGirl01',
    fileName: 'jpngirl01.mdl',
    url: jpngirlModel
  },
  {
    id: 'v_ak47',
    label: 'AK-47 viewmodel',
    fileName: 'v_ak47.mdl',
    url: vAk47Model,
    preferredViewMode: 'weapon-origin'
  }
]

const getInitialDemoModel = () => {
  const params = new URLSearchParams(window.location.search)

  if (!params.has('demo')) {
    return undefined
  }

  const requestedDemoId = params.get('demo') || 'leet'

  return DEMO_MODELS.find(demoModel =>
    demoModel.id === requestedDemoId
    || demoModel.fileName === requestedDemoId
    || demoModel.fileName.replace(/\.mdl$/i, '') === requestedDemoId
  ) ?? DEMO_MODELS[0]
}

type AppContentProps = {
  buffer: ArrayBuffer | null
  isLoading: boolean
  loadError: string | null
  loadingDescription?: string
  demoModels: DemoModel[]
  cameraPreset?: CameraUrlPreset
  modelController?: ModelController
  modelData?: ModelData
  playbackPreset: PlaybackUrlPreset
  embedModelUrl?: string
  cacheStats?: ModelCacheStats | null
  isCacheStatsLoading?: boolean
  onCacheStatsRequest?: (signal?: AbortSignal) => void
  shareableModelUrl?: string
  isEmbed: boolean
  showOptionsMenu: boolean
  viewMode: ViewMode
  defaultBackgroundColor?: string
  viewerPose?: ViewerPose
  openDemoModel: (demoModel: DemoModel) => void
  openHome: () => void
  openRemoteModelUrl: (modelUrl: string) => Promise<void>
  setFile: (file: File) => void
  setModelController: (controller?: ModelController) => void
  setModelData: (modelData?: ModelData) => void
  setViewerPose: (viewerPose: ViewerPose) => void
  setViewMode: (viewMode: ViewMode) => void
}

const AppContent = (props: AppContentProps) => {
  const onDropAccepted = React.useCallback(
    (files: File[]) => {
      const [file] = files

      if (file) {
        props.setFile(file)
      }
    },
    [props]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      'application/octet-stream': ['.mdl']
    },
    multiple: false,
    noClick: true,
    onDropAccepted
  })

  return (
    <div {...getRootProps()}>
      <BackgroundContainer defaultColor={props.defaultBackgroundColor}>
        {({ backgroundColor }, { setBackgroundColor }) => (
          <React.Fragment>
            <GlobalStyles backgroundColor={backgroundColor} color="#fff" />

            {props.buffer && !props.isLoading && !props.isEmbed ? (
              <FpsCounter showHomeButton onHomeClick={props.openHome} />
            ) : null}

            {props.showOptionsMenu && props.buffer && !props.isLoading && props.modelController && props.modelData ? (
              <Controller
                isLoading={props.isLoading}
                backgroundColor={backgroundColor}
                embedModelUrl={props.embedModelUrl}
                modelController={props.modelController}
                modelData={props.modelData}
                shareableModelUrl={props.shareableModelUrl}
                viewerPose={props.viewerPose}
                viewMode={props.viewMode}
                onBackgroundColorUpdate={setBackgroundColor}
                onModelLoad={props.setFile}
                onViewModeChange={props.setViewMode}
              />
            ) : null}

            {(isDragActive || (!props.buffer && !props.isLoading)) && (
              <Dropzone backgroundColor={backgroundColor} isDragActive={isDragActive} inputProps={getInputProps()}>
                {isDragActive
                  ? 'Drop your `.mdl` to launch the viewer.'
                  : (
                    <StartScreen
                      demoModels={props.demoModels}
                      loadError={props.loadError}
                      cacheStats={props.cacheStats}
                      isCacheStatsLoading={props.isCacheStatsLoading}
                      onCacheStatsRequest={props.onCacheStatsRequest}
                      openDemoModel={props.openDemoModel}
                      openRemoteModelUrl={props.openRemoteModelUrl}
                      selectFile={open}
                    />
                  )}
              </Dropzone>
            )}

            {props.isLoading && (
              <LoadingScreen
                title="Loading file"
                description={props.loadingDescription ?? 'Reading the model from disk before the viewer starts.'}
              />
            )}

            {props.buffer && !props.isLoading && (
              <React.Suspense
                fallback={
                  <LoadingScreen
                    title="Starting viewer"
                    description="Loading the 3D renderer only when a model is actually needed."
                    progress={0.18}
                  />
                }
              >
                <Renderer
                  modelBuffer={props.buffer}
                  cameraPreset={props.cameraPreset}
                  playbackPreset={props.playbackPreset}
                  onViewerPoseChange={props.setViewerPose}
                  setModelController={props.setModelController}
                  setModelData={props.setModelData}
                  viewMode={props.viewMode}
                />
              </React.Suspense>
            )}
          </React.Fragment>
        )}
      </BackgroundContainer>
    </div>
  )
}

export const App = () => {
  const [urlConfig] = React.useState(() => parseUrlConfig())
  const [initialDemoModel] = React.useState(() => (urlConfig.modelQuery ? undefined : getInitialDemoModel()))
  const [isInitialDemoSuppressed, setInitialDemoSuppressed] = React.useState(false)
  const [directModelSourceUrl, setDirectModelSourceUrl] = React.useState<string>()
  const [cachedModelFileUrl, setCachedModelFileUrl] = React.useState<string>()
  const [cacheStats, setCacheStats] = React.useState<ModelCacheStats | null>(null)
  const [isCacheStatsLoading, setCacheStatsLoading] = React.useState(true)
  const cacheStatsRequestRef = React.useRef<Promise<void> | null>(null)
  const cacheStatsVersionRef = React.useRef(0)
  const [modelCacheState, setModelCacheState] = React.useState({
    isLoading: Boolean(urlConfig.modelQuery),
    error:     null as string | null
  })
  const [modelController, setModelController] = React.useState<ModelController>()
  const [modelData, setModelData] = React.useState<ModelData>()
  const [shareableModelUrl, setShareableModelUrl] = React.useState(
    urlConfig.modelQuery && isModelCacheId(urlConfig.modelQuery) ? urlConfig.modelQuery : undefined
  )
  const [viewerPose, setViewerPose] = React.useState<ViewerPose>()
  const [viewMode, setViewMode] = React.useState<ViewMode>(
    urlConfig.viewMode ?? initialDemoModel?.preferredViewMode ?? 'free-view'
  )
  const clearShareableModelUrl = React.useCallback(() => {
    setShareableModelUrl(undefined)

    const nextUrl = createUrlWithoutShareParams()

    if (window.location.href !== nextUrl) {
      window.history.replaceState(window.history.state, '', nextUrl)
    }
  }, [])

  const clearHomeUrl = React.useCallback(() => {
    setShareableModelUrl(undefined)

    const nextUrl = createHomeUrl()

    if (window.location.href !== nextUrl) {
      window.history.replaceState(window.history.state, '', nextUrl)
    }
  }, [])

  const applyModelCacheStats = React.useCallback((stats?: ModelCacheStats) => {
    if (!stats) {
      return
    }

    cacheStatsVersionRef.current += 1
    setCacheStats(stats)
    setCacheStatsLoading(false)
  }, [])

  const requestModelCacheStats = React.useCallback((signal?: AbortSignal) => {
    if (cacheStats || cacheStatsRequestRef.current) {
      return
    }

    setCacheStatsLoading(true)
    const requestVersion = cacheStatsVersionRef.current
    let request: Promise<void>

    request = getModelCacheStats(signal)
      .then(nextStats => {
        if (!signal?.aborted && requestVersion === cacheStatsVersionRef.current) {
          setCacheStats(nextStats)
        }
      })
      .catch(() => {
        if (!signal?.aborted && requestVersion === cacheStatsVersionRef.current) {
          setCacheStats(null)
        }
      })
      .finally(() => {
        if (cacheStatsRequestRef.current === request) {
          cacheStatsRequestRef.current = null
        }

        if (!signal?.aborted) {
          setCacheStatsLoading(false)
        }
      })

    cacheStatsRequestRef.current = request
  }, [cacheStats])

  const openRemoteModelUrl = React.useCallback(async (modelUrl: string) => {
    const parsedModelUrl = parseModelUrl(modelUrl, window.location.href)

    if (!parsedModelUrl) {
      throw new Error('Use a direct http(s) link ending in .mdl.')
    }

    setCachedModelFileUrl(undefined)
    setModelController(undefined)
    setModelData(undefined)
    setViewerPose(undefined)
    setShareableModelUrl(undefined)
    setDirectModelSourceUrl(undefined)
    setModelCacheState({ isLoading: true, error: null })

    try {
      const cachedModel = await cacheRemoteModelUrl(parsedModelUrl)
      const nextUrl = createUrlWithModelQuery(cachedModel.id)

      window.history.pushState(window.history.state, '', nextUrl)
      setShareableModelUrl(cachedModel.id)
      setCachedModelFileUrl(cachedModel.fileUrl)
      setDirectModelSourceUrl(parseModelUrl(cachedModel.sourceUrl ?? '', window.location.href) ?? parsedModelUrl)
      setModelCacheState({ isLoading: false, error: null })
      applyModelCacheStats(cachedModel.stats)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not cache this model.'

      setDirectModelSourceUrl(undefined)
      setModelCacheState({ isLoading: false, error: errorMessage })
      throw error
    }
  }, [applyModelCacheStats])

  React.useEffect(() => {
    if (!urlConfig.modelQuery) {
      return
    }

    const modelQuery = urlConfig.modelQuery
    const abortController = new AbortController()

    const loadModelFromQuery = async () => {
      setModelCacheState({ isLoading: true, error: null })

      try {
        const parsedModelUrl = parseModelUrl(modelQuery, window.location.href)
        const isDirectModelQuery = Boolean(parsedModelUrl)

        if (!isModelCacheId(modelQuery) && !parsedModelUrl) {
          throw new Error('Use a valid cached model id or a direct http(s) .mdl link.')
        }

        const cachedModel = isModelCacheId(modelQuery)
          ? await resolveCachedModel(modelQuery, abortController.signal)
          : await cacheRemoteModelUrl(parsedModelUrl ?? '', abortController.signal)
        const sourceModelUrl = parseModelUrl(cachedModel.sourceUrl ?? '', window.location.href)

        if (abortController.signal.aborted) {
          return
        }

        const nextUrl = createUrlWithModelQuery(cachedModel.id)

        if (window.location.href !== nextUrl) {
          window.history.replaceState(window.history.state, '', nextUrl)
        }

        setShareableModelUrl(cachedModel.id)
        setCachedModelFileUrl(cachedModel.fileUrl)
        setDirectModelSourceUrl(sourceModelUrl ?? (isDirectModelQuery ? parsedModelUrl : undefined))
        setModelCacheState({ isLoading: false, error: null })
        applyModelCacheStats(cachedModel.stats)
      } catch (error) {
        if (abortController.signal.aborted) {
          return
        }

        const parsedModelUrl = parseModelUrl(modelQuery, window.location.href)
        const errorMessage = parsedModelUrl || isModelCacheId(modelQuery)
          ? error instanceof Error ? error.message : 'Could not cache this model.'
          : 'Use a valid cached model id or a direct http(s) .mdl link.'

        setCachedModelFileUrl(undefined)
        setShareableModelUrl(undefined)
        setDirectModelSourceUrl(undefined)
        setModelCacheState({ isLoading: false, error: errorMessage })
      }
    }

    void loadModelFromQuery()

    return () => abortController.abort()
  }, [applyModelCacheStats, urlConfig.modelQuery])

  const defaultFileUrl = cachedModelFileUrl ?? (isInitialDemoSuppressed ? undefined : initialDemoModel?.url)

  return (
    <FileContainer defaultFileUrl={defaultFileUrl}>
      {({ buffer, isLoading, loadError }, { clearFile, setFile, setFileUrl }) => (
        <AppContent
          buffer={buffer}
          isLoading={modelCacheState.isLoading || isLoading}
          loadingDescription={modelCacheState.isLoading
            ? 'Caching and validating the remote model before the viewer starts.'
            : undefined}
          loadError={modelCacheState.error ?? loadError}
          demoModels={DEMO_MODELS}
          cameraPreset={urlConfig.cameraPreset}
          embedModelUrl={directModelSourceUrl}
          cacheStats={cacheStats}
          isCacheStatsLoading={isCacheStatsLoading}
          onCacheStatsRequest={requestModelCacheStats}
          modelController={modelController}
          modelData={modelData}
          playbackPreset={urlConfig.playbackPreset}
          shareableModelUrl={shareableModelUrl}
          isEmbed={urlConfig.isEmbed}
          showOptionsMenu={urlConfig.showOptionsMenu}
          viewMode={viewMode}
          defaultBackgroundColor={urlConfig.backgroundColor}
          viewerPose={viewerPose}
          openDemoModel={demoModel => {
            clearShareableModelUrl()
            setInitialDemoSuppressed(true)
            setDirectModelSourceUrl(undefined)
            setModelController(undefined)
            setModelData(undefined)
            setViewerPose(undefined)
            setViewMode(demoModel.preferredViewMode ?? 'free-view')
            setFileUrl(demoModel.url)
          }}
          openHome={() => {
            clearHomeUrl()
            clearFile()
            setInitialDemoSuppressed(true)
            setCachedModelFileUrl(undefined)
            setModelController(undefined)
            setModelData(undefined)
            setViewerPose(undefined)
            setDirectModelSourceUrl(undefined)
            setModelCacheState({ isLoading: false, error: null })
          }}
          openRemoteModelUrl={openRemoteModelUrl}
          setFile={file => {
            clearShareableModelUrl()
            setInitialDemoSuppressed(true)
            setDirectModelSourceUrl(undefined)
            setModelController(undefined)
            setModelData(undefined)
            setViewerPose(undefined)
            setFile(file)
          }}
          setModelController={setModelController}
          setModelData={setModelData}
          setViewerPose={setViewerPose}
          setViewMode={setViewMode}
        />
      )}
    </FileContainer>
  )
}
